import { useEffect, useState } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { cn } from '@/lib/utils'

type Mode = 'light' | 'dark' | 'system'

const MODES: { value: Mode; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

/**
 * 3-way mode selector — writes to next-themes which drives
 * `<html data-mode="light|dark|system">` per DESIGN.md convention.
 *
 * next-themes reads `localStorage` / `matchMedia` on mount, so we defer rendering
 * the active state until after hydration to avoid SSR/client mismatches and to
 * keep the selected pill in sync with the resolved mode.
 */
export function AppearanceSection() {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const active = (mounted ? theme : undefined) as Mode | undefined

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Choose how IntelHub looks. System follows your device setting.
      </p>
      <div
        role="radiogroup"
        aria-label="Color mode"
        className="inline-flex flex-wrap gap-2"
      >
        {MODES.map(({ value, label, icon: Icon }) => {
          const selected = active === value
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setTheme(value)}
              className={cn(
                'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border bg-background text-foreground hover:bg-accent',
              )}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
